import React, { useState, useRef, useEffect, FC, ReactNode } from 'react';
import styles from './MarkdownEditor.module.scss';
import { Button, IconButton, Tooltip } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  rows?: number;
  className?: string;
  headerContent?: ReactNode; // Add this prop to accept a custom header element
  mode?: 'full' | 'plain';
}

const MarkdownEditor: FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your cover letter...",
  label = "Cover Letter",
  error = false,
  helperText,
  rows = 10,
  className,
  headerContent, // Destructure the new prop
  mode = 'full',
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertMarkdown = (before: string, after: string = '', placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const newValue = 
      value.substring(0, start) + 
      before + textToInsert + after + 
      value.substring(end);
    
    onChange(newValue);
    
    // Set cursor position after the inserted text
    setTimeout(() => {
      if (selectedText) {
        textarea.setSelectionRange(start + before.length, start + before.length + textToInsert.length);
      } else {
        textarea.setSelectionRange(start + before.length, start + before.length + placeholder.length);
      }
      textarea.focus();
    }, 0);
  };

  const handleBold = () => {
    insertMarkdown('**', '**', 'bold text');
  };

  const handleItalic = () => {
    insertMarkdown('*', '*', 'italic text');
  };

  const handleBulletList = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const beforeCursor = value.substring(0, start);
    const afterCursor = value.substring(start);
    
    // Check if we're at the beginning of a line
    const lineStart = beforeCursor.lastIndexOf('\n') + 1;
    const isAtLineStart = start === lineStart;
    
    let newValue;
    if (isAtLineStart) {
      newValue = beforeCursor + '- ' + afterCursor;
      onChange(newValue);
      setTimeout(() => {
        textarea.setSelectionRange(start + 2, start + 2);
        textarea.focus();
      }, 0);
    } else {
      newValue = beforeCursor + '\n- ' + afterCursor;
      onChange(newValue);
      setTimeout(() => {
        textarea.setSelectionRange(start + 3, start + 3);
        textarea.focus();
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const beforeCursor = value.substring(0, start);
      const lineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(lineStart);
      
      // Auto-continue bullet lists
      if (currentLine.match(/^- /)) {
        e.preventDefault();
        const afterCursor = value.substring(start);
        const newValue = beforeCursor + '\n- ' + afterCursor;
        onChange(newValue);
        setTimeout(() => {
          textarea.setSelectionRange(start + 3, start + 3);
        }, 0);
      }
    }
  };

  return (
    <div className={`${styles.markdownEditor} ${className || ''}`}>
      <div className={styles.header}>
        <label className={styles.label}>{label}</label>
        {headerContent} {/* Render the custom header content here */}
        {mode === 'full' && (
          <div className={styles.toolbar}>
            <>
              <Tooltip title="Bold">
                <IconButton 
                  size="small" 
                  onClick={handleBold}
                  disabled={isPreviewMode}
                  className={styles.toolbarButton}
                >
                  <FormatBold />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic">
                <IconButton 
                  size="small" 
                  onClick={handleItalic}
                  disabled={isPreviewMode}
                  className={styles.toolbarButton}
                >
                  <FormatItalic />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bullet List">
                <IconButton 
                  size="small" 
                  onClick={handleBulletList}
                  disabled={isPreviewMode}
                  className={styles.toolbarButton}
                >
                  <FormatListBulleted />
                </IconButton>
              </Tooltip>
              <div className={styles.divider} />
            </>
            <Tooltip title={isPreviewMode ? "Edit" : "Preview"}>
              <IconButton 
                size="small" 
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className={`${styles.toolbarButton} ${isPreviewMode ? styles.active : ''}`}
              >
                {isPreviewMode ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      
      <div className={styles.editorContainer}>
        {mode === 'plain' ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={rows}
            className={`${styles.textarea} ${error ? styles.error : ''}`}
          />
        ) : (
          isPreviewMode ? (
            <div className={`${styles.preview} ${error ? styles.error : ''}`}>
              <div className={styles.markdownContent}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {value || '*No content to preview*'}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={rows}
              className={`${styles.textarea} ${error ? styles.error : ''}`}
            />
          )
        )}
      </div>
      
      {helperText && (
        <div className={`${styles.helperText} ${error ? styles.errorText : ''}`}>
          {helperText}
        </div>
      )}
      
      {mode === 'full' && (
        <div className={styles.formatHelp}>
          <small>
            <strong>Formatting tips:</strong> Use **bold**, *italic*, and bullet lists with -
          </small>
        </div>
      )}
    </div>
  );
};

export default MarkdownEditor; 